import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, faUser, faEnvelope, faPhone, faCalendarDays, 
  faUserPlus, faPeopleArrows, faTriangleExclamation, faHeartPulse,
  faCheck, faArrowRight, faHome, faBell, faCamera, faIdCard,
  faInfoCircle, faTrophy, faLock, faHashtag, faShirt
} from '@fortawesome/free-solid-svg-icons';
import { determineMTBCategory, calculateAgeInCurrentYear } from '../utils/categoryUtils';
import { userService } from '../services/database';
import { loginUser } from '../utils/auth';

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [dateOfBirth, setDateOfBirth] = useState<string>('');
  const [gender, setGender] = useState<'male' | 'female'>('male');
  const [ageThisYear, setAgeThisYear] = useState<number | null>(null);
  const [riderCategory, setRiderCategory] = useState<any>(null);
  
  // Form data state
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    phone: '',
    idNumber: '',
    tshirtSize: '',
    csaLicence: '',
    emergencyName: '',
    emergencyPhone: '',
    relationship: '',
    allergies: '',
    medicalConditions: ''
  });
  
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Determine race category when date of birth or gender changes
  useEffect(() => {
    if (dateOfBirth) {
      const dob = new Date(dateOfBirth);
      const calculatedAge = calculateAgeInCurrentYear(dob);
      setAgeThisYear(calculatedAge);
      
      const category = determineMTBCategory(dob, gender);
      setRiderCategory(category);
    } else {
      setAgeThisYear(null);
      setRiderCategory(null);
    }
  }, [dateOfBirth, gender]);

  // Handle form input changes
  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear errors when user starts typing
    if (formErrors.length > 0) {
      setFormErrors([]);
    }
  };

  // Validate form data
  const validateForm = (): string[] => {
    const errors: string[] = [];
    
    if (!formData.fullName.trim()) errors.push('Full name is required');
    if (!formData.email.trim()) errors.push('Email address is required');
    if (!formData.password.trim()) errors.push('Password is required');
    if (formData.password && formData.password.length < 6) errors.push('Password must be at least 6 characters long');
    if (!formData.phone.trim()) errors.push('Phone number is required');
    if (!formData.idNumber.trim()) errors.push('ID number is required');
    if (!dateOfBirth) errors.push('Date of birth is required');
    if (!formData.emergencyName.trim()) errors.push('Emergency contact name is required');
    if (!formData.emergencyPhone.trim()) errors.push('Emergency contact phone is required');
    if (!riderCategory) errors.push('Please ensure your date of birth and gender are correctly entered to determine your race category');
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      errors.push('Please enter a valid email address');
    }
    
    // ID Number validation
    if (formData.idNumber && (formData.idNumber.length !== 13 || !/^\d{13}$/.test(formData.idNumber))) {
      errors.push('ID number must be exactly 13 digits');
    }
    
    return errors;
  };

  // Handle continue to account setup
  const handleContinueToAccountSetup = async () => {
    const errors = validateForm();
    
    if (errors.length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Check if user already exists with this email
      const existingUser = await userService.getUserByEmail(formData.email);
      
      if (existingUser) {
        setFormErrors(['An account with this email address already exists. Please use a different email or sign in.']);
        setIsSubmitting(false);
        return;
      }

      // Save ALL registration data to database (after running the SQL update script)
      const userData = {
        email: formData.email,
        name: formData.fullName,
        phone: formData.phone,
        id_number: formData.idNumber || null, // Allow null for backward compatibility
        tshirt_size: formData.tshirtSize || null,
        date_of_birth: dateOfBirth,
        gender: gender,
        rider_category: riderCategory?.category || '',
        csa_licence: formData.csaLicence || null,
        emergency_contact_name: formData.emergencyName,
        emergency_contact_phone: formData.emergencyPhone,
        emergency_contact_relationship: formData.relationship || null,
        allergies: formData.allergies || 'None',
        medical_conditions: formData.medicalConditions || 'None'
      };

      console.log('Creating user with complete registration data:', userData);
      
      // Create user with ALL the registration data
      const newUser = await userService.createUserFlexible(userData);

      // Store password for login (in production, this would be hashed and stored in database)
      localStorage.setItem(`gomtb_password_${formData.email}`, formData.password);

      // Store user session using auth utility
      loginUser({
        id: newUser.id,
        email: newUser.email,
        name: newUser.name
      });
      
      // Navigate to dashboard
      navigate('/dashboard');
      
    } catch (error: any) {
      console.error('Registration error:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
        formData: formData,
        dateOfBirth: dateOfBirth,
        gender: gender,
        riderCategory: riderCategory
      });
      
      // Show more specific error message
      let errorMessage = 'Registration failed. ';
      if (error.message) {
        errorMessage += `Error: ${error.message}`;
      } else {
        errorMessage += 'Please check your database connection and try again.';
      }
      
      setFormErrors([errorMessage]);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-gray-100 font-sans text-dark">
      {/* Mobile Header */}
      <header className="app-header">
        <div className="flex items-center justify-between">
          <Link to="/" className="app-header-back">
            <FontAwesomeIcon icon={faArrowLeft} className="text-lg" />
          </Link>
          <h1 className="app-header-title">Register</h1>
          <div className="w-6"></div> {/* Empty div for spacing */}
        </div>
      </header>

      {/* Main Content */}
      <main className="content-container pb-20">
        {/* Progress Indicator */}
        <section className="px-4 py-3 bg-white border-b">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-primary h-2 rounded-full w-2/5"></div>
          </div>
          <p className="text-xs text-right mt-1 text-gray-500">Step 1 of 3</p>
        </section>

        {/* Error Display */}
        {formErrors.length > 0 && (
          <section className="px-4 py-3 bg-red-50 border-b border-red-200">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              <div className="flex items-start">
                <FontAwesomeIcon icon={faTriangleExclamation} className="mr-2 mt-0.5" />
                <div>
                  <p className="font-medium">Please correct the following errors:</p>
                  <ul className="mt-2 list-disc list-inside text-sm">
                    {formErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Registration Form */}
        <form className="px-4 py-6">
          {/* Profile Photo Upload */}
          <section className="mb-6 flex flex-col items-center">
            <div className="relative mb-2">
              <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-200 border-2 border-primary flex items-center justify-center">
                <FontAwesomeIcon icon={faUser} className="text-gray-400 text-4xl" />
              </div>
              <button type="button" className="absolute bottom-0 right-0 bg-primary text-white rounded-full p-2 shadow">
                <FontAwesomeIcon icon={faCamera} />
              </button>
            </div>
            <p className="text-sm text-gray-500">Profile Photo (Optional)</p>
          </section>

          {/* Personal Information */}
          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-4">Personal Information</h2>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
                    <FontAwesomeIcon icon={faUser} />
                  </span>
                  <input 
                    type="text" 
                    id="fullName" 
                    name="fullName" 
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary" 
                    placeholder="Enter your full name" 
                    required 
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
                    <FontAwesomeIcon icon={faEnvelope} />
                  </span>
                  <input 
                    type="email" 
                    id="email" 
                    name="email" 
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary" 
                    placeholder="Enter your email" 
                    required 
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
                    <FontAwesomeIcon icon={faLock} />
                  </span>
                  <input 
                    type="password" 
                    id="password" 
                    name="password" 
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary" 
                    placeholder="Create a password (min 6 characters)" 
                    required 
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Password must be at least 6 characters long</p>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
                    <FontAwesomeIcon icon={faPhone} />
                  </span>
                  <input 
                    type="tel" 
                    id="phone" 
                    name="phone" 
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary" 
                    placeholder="(555) 123-4567" 
                    required 
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="idNumber" className="block text-sm font-medium text-gray-700 mb-1">ID Number *</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
                    <FontAwesomeIcon icon={faHashtag} />
                  </span>
                  <input 
                    type="text" 
                    id="idNumber" 
                    name="idNumber" 
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary" 
                    placeholder="Enter your 13-digit ID number" 
                    maxLength={13}
                    pattern="[0-9]*"
                    required
                    value={formData.idNumber}
                    onChange={(e) => {
                      // Only allow numbers
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      if (value.length <= 13) {
                        handleInputChange('idNumber', value);
                      }
                    }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">South African ID number (13 digits required)</p>
              </div>

              <div>
                <label htmlFor="tshirtSize" className="block text-sm font-medium text-gray-700 mb-1">T-Shirt Size</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
                    <FontAwesomeIcon icon={faShirt} />
                  </span>
                  <select 
                    id="tshirtSize" 
                    name="tshirtSize" 
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary appearance-none bg-white"
                    value={formData.tshirtSize}
                    onChange={(e) => handleInputChange('tshirtSize', e.target.value)}
                  >
                    <option value="">Select size (optional)</option>
                    <option value="XS">XS - Extra Small</option>
                    <option value="S">S - Small</option>
                    <option value="M">M - Medium</option>
                    <option value="L">L - Large</option>
                    <option value="XL">XL - Extra Large</option>
                    <option value="XXL">XXL - 2X Large</option>
                    <option value="XXXL">XXXL - 3X Large</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg className="w-4 h-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" fillRule="evenodd"></path>
                    </svg>
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="dob" className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
                    <FontAwesomeIcon icon={faCalendarDays} />
                  </span>
                  <input 
                    type="date" 
                    id="dob" 
                    name="dob" 
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary" 
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    required 
                  />
                </div>
                {dateOfBirth && ageThisYear !== null && (
                  <p className="text-sm text-primary mt-1">
                    <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                    Age in {new Date().getFullYear()}: {ageThisYear} years
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="csaLicence" className="block text-sm font-medium text-gray-700 mb-1">CSA Licence</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
                    <FontAwesomeIcon icon={faIdCard} />
                  </span>
                  <input 
                    type="text" 
                    id="csaLicence" 
                    name="csaLicence" 
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary" 
                    placeholder="Enter CSA Licence number (if available)" 
                    value={formData.csaLicence}
                    onChange={(e) => handleInputChange('csaLicence', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Emergency Contact */}
          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-4">Emergency Contact</h2>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="emergencyName" className="block text-sm font-medium text-gray-700 mb-1">Contact Name *</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
                    <FontAwesomeIcon icon={faUserPlus} />
                  </span>
                  <input 
                    type="text" 
                    id="emergencyName" 
                    name="emergencyName" 
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary" 
                    placeholder="Emergency contact name" 
                    required 
                    value={formData.emergencyName}
                    onChange={(e) => handleInputChange('emergencyName', e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="emergencyPhone" className="block text-sm font-medium text-gray-700 mb-1">Contact Number *</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
                    <FontAwesomeIcon icon={faPhone} />
                  </span>
                  <input 
                    type="tel" 
                    id="emergencyPhone" 
                    name="emergencyPhone" 
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary" 
                    placeholder="(555) 123-4567" 
                    required 
                    value={formData.emergencyPhone}
                    onChange={(e) => handleInputChange('emergencyPhone', e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="relationship" className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
                    <FontAwesomeIcon icon={faPeopleArrows} />
                  </span>
                  <select 
                    id="relationship" 
                    name="relationship" 
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary appearance-none bg-white"
                    defaultValue=""
                    value={formData.relationship}
                    onChange={(e) => handleInputChange('relationship', e.target.value)}
                  >
                    <option value="" disabled>Select relationship</option>
                    <option value="spouse">Spouse</option>
                    <option value="parent">Parent</option>
                    <option value="sibling">Sibling</option>
                    <option value="friend">Friend</option>
                    <option value="other">Other</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg className="w-4 h-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" fillRule="evenodd"></path>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Medical Information */}
          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-4">Medical Information</h2>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="allergies" className="block text-sm font-medium text-gray-700 mb-1">Allergies</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                  </span>
                  <input 
                    type="text" 
                    id="allergies" 
                    name="allergies" 
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary" 
                    placeholder="List any allergies (if none, write 'None')" 
                    value={formData.allergies}
                    onChange={(e) => handleInputChange('allergies', e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="medicalConditions" className="block text-sm font-medium text-gray-700 mb-1">Medical Conditions</label>
                <div className="relative">
                  <span className="absolute top-3 left-3 text-gray-500">
                    <FontAwesomeIcon icon={faHeartPulse} />
                  </span>
                  <textarea 
                    id="medicalConditions" 
                    name="medicalConditions" 
                    rows={3} 
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary" 
                    placeholder="List any medical conditions we should know about"
                    value={formData.medicalConditions}
                    onChange={(e) => handleInputChange('medicalConditions', e.target.value)}
                  ></textarea>
                </div>
              </div>
            </div>
          </section>

          {/* Gender Selection */}
          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-4">Gender</h2>
            
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select your gender for race category determination *</label>
              
              <div className="relative">
                <input 
                  type="radio" 
                  name="gender" 
                  id="male" 
                  value="male" 
                  className="absolute opacity-0 w-full h-full cursor-pointer"
                  onChange={() => setGender('male')}
                  checked={gender === 'male'}
                />
                <label 
                  htmlFor="male" 
                  className={`flex items-center p-3 border rounded-lg cursor-pointer ${
                    gender === 'male' 
                      ? 'border-primary bg-orange-50' 
                      : 'border-gray-300'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full border flex items-center justify-center mr-3 ${
                    gender === 'male'
                      ? 'border-primary bg-primary'
                      : 'border-gray-300'
                  }`}>
                    {gender === 'male' && (
                      <FontAwesomeIcon icon={faCheck} className="text-white text-sm" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-medium">Male</h3>
                  </div>
                </label>
              </div>
              
              <div className="relative">
                <input 
                  type="radio" 
                  name="gender" 
                  id="female" 
                  value="female" 
                  className="absolute opacity-0 w-full h-full cursor-pointer"
                  onChange={() => setGender('female')}
                  checked={gender === 'female'}
                />
                <label 
                  htmlFor="female" 
                  className={`flex items-center p-3 border rounded-lg cursor-pointer ${
                    gender === 'female' 
                      ? 'border-primary bg-orange-50' 
                      : 'border-gray-300'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full border flex items-center justify-center mr-3 ${
                    gender === 'female'
                      ? 'border-primary bg-primary'
                      : 'border-gray-300'
                  }`}>
                    {gender === 'female' && (
                      <FontAwesomeIcon icon={faCheck} className="text-white text-sm" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-medium">Female</h3>
                  </div>
                </label>
              </div>
            </div>
          </section>
          
          {/* Race Category */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold mb-4">Race Category</h2>
            
            {riderCategory ? (
              <div className="bg-orange-50 border border-primary rounded-lg p-4">
                <div className="flex items-start">
                  <FontAwesomeIcon icon={faTrophy} className="text-primary mt-1 mr-3" />
                  <div>
                    <h3 className="font-bold text-primary">{riderCategory.category} ({riderCategory.code})</h3>
                    <p className="text-sm text-gray-700 mt-1">
                      Based on your date of birth, you will be {ageThisYear} years old in {new Date().getFullYear()}.
                      According to UCI rules, you belong to the {riderCategory.category} category.
                    </p>
                    <p className="text-sm text-gray-700 mt-2">
                      Age range: {riderCategory.minAge}-{riderCategory.maxAge === 200 ? '+' : riderCategory.maxAge} years
                    </p>
                    
                    <div className="mt-3 pt-3 border-t border-blue-200">
                      <p className="text-sm text-gray-600">
                        <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                        Race categories are determined by the age you will turn during the current calendar year,
                        regardless of when your actual birthday falls.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : dateOfBirth ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-700">
                  <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                  No race category could be determined. Please ensure your date of birth and gender are correctly entered.
                </p>
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-600">
                  <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                  Enter your date of birth and gender above to automatically determine your race category.
                </p>
                <Link to="/categories" className="text-primary text-sm inline-block mt-2">
                  View all race categories
                </Link>
              </div>
            )}
          </section>

          {/* Continue Button */}
          <div className="mb-4">
            <button 
              type="button" 
              className="w-full bg-primary text-white font-medium py-3 rounded-lg shadow-md hover:bg-orange-600 transition duration-300 flex items-center justify-center"
              disabled={!riderCategory || isSubmitting}
              onClick={handleContinueToAccountSetup}
            >
              {isSubmitting ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-4 border-b-4 border-white"></div>
              ) : (
                <>
                  Continue to Account Setup
                  <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                </>
              )}
            </button>
          </div>
          
          <p className="text-center text-xs text-gray-500">
            By continuing, you agree to our <span className="text-primary cursor-pointer">Terms of Service</span> and <span className="text-primary cursor-pointer">Privacy Policy</span>
          </p>
        </form>
      </main>

      {/* Bottom Navigation */}
      <nav className="app-bottom-nav">
        <div className="flex justify-around">
          <Link to="/" className="app-bottom-nav-link py-2 px-3">
            <FontAwesomeIcon icon={faHome} className="text-lg" />
            <span className="text-xs mt-1">Home</span>
          </Link>
          <Link to="/events" className="app-bottom-nav-link py-2 px-3">
            <FontAwesomeIcon icon={faCalendarDays} className="text-lg" />
            <span className="text-xs mt-1">Events</span>
          </Link>
          <Link to="/register" className="app-bottom-nav-link-active py-2 px-3">
            <FontAwesomeIcon icon={faUser} className="text-lg" />
            <span className="text-xs mt-1">Profile</span>
          </Link>
          <span className="app-bottom-nav-link py-2 px-3 cursor-pointer">
            <FontAwesomeIcon icon={faBell} className="text-lg" />
            <span className="text-xs mt-1">Alerts</span>
          </span>
        </div>
      </nav>
    </div>
  );
};

export default Register;
